import React from "react"
import LoadingState from "Components/organisms/loading-state"
import Project, { ProjectFinance } from "models/project"
import { Container } from "reactstrap"
import DetailPageHeader from "Components/molecule/cards/detail-page-header"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import AcumaticaLogo from "assets/images/brands/acumatica.png"
import Tabs from "Components/molecule/tabs"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import DataTable from "Components/molecule/data-table"
import ProjectTask from "models/project-task"
import ProjectBudget from "models/project-budget"
import ClientProjectFinance from "models/project-finance"
import Transaction from "models/transaction"
import { ColumnDef, Table } from "@tanstack/react-table"
import FinancialCard from "Components/molecule/cards/financial-card"
import BillingCollectionsCard from "Components/molecule/cards/billing-collections-card"
import PerformanceSnapshotCard from "Components/molecule/cards/performance-snapshot-card"
import { formatDateTimeAmPm, getMonthYearLabel } from "utils/tools"
import FinancialsAndForecasting, { FinancialAndForecastingRow } from "Components/organisms/financials-and-forecasting"
import ProjectChangeLog from "Components/organisms/project-change-log"
import { ChangeOrder } from "models/change-order"
import EarnedValueAnalysis from "Components/organisms/earned-value-analysis"
import ActionButton from "Components/atoms/buttons/action-button"
import Breadcrumb from "Components/organisms/breadcrumb"
import { SyncIcon, HelpIcon } from "Components/atoms/icons"
import ProjectHealthPanel from "Components/organisms/project-health"
import ProjectHealthSummary from "Components/molecule/project-health-summary"
import { ProjectHealth, ProjectHealthHistoryItem } from "models/project-health"
import { EffectiveMarginTarget } from "models/project-margin-target"


interface ProjectDetailsTemplateProps {
    project: Project | null
    isLoadingProject: boolean
    activeTab: string,
    setActiveTab: React.Dispatch<React.SetStateAction<string>>,
    openSyncModal: () => any
    closeSyncMOdal: () => any
    handleSyncData: () => any
    handleNavigateToAcumatica: (_project: Project) => any
    handleSaveProjectFinancialAndForecasting: () => Promise<any>
    handleResetProjectFinancialAndForecasting: () => any,
    isSyncingData: boolean
    isSyncModalOpen: boolean
    taskTable: Table<ProjectTask>
    taskColumns: ColumnDef<ProjectTask>[]
    revenueTable: Table<ProjectBudget>
    revenueColumns: ColumnDef<ProjectBudget>[]
    costTable: Table<ProjectBudget>
    costColumns: ColumnDef<ProjectBudget>[]
    transactionTable: Table<Transaction>
    transactionColumns: ColumnDef<Transaction>[]
    projectFinances: ProjectFinance | null
    projectChangeLogTable: Table<ChangeOrder>
    projectChangeLogColumns: ColumnDef<ChangeOrder>[]
    financialAndForecastingTable: Table<FinancialAndForecastingRow>
    financialAndForecastingColumns: ColumnDef<FinancialAndForecastingRow>[]
    financialAndForecastingControlsId: string
    transactionTotalItems: number
    handleOpenHelpDocument?: () => any,
    projectHealth: ProjectHealth | null
    projectHealthHistory: ProjectHealthHistoryItem[]
    isLoadingProjectHealth: boolean
    marginTarget: EffectiveMarginTarget | null
}


const ProjectDetailsTemplate = ({
    project,
    isLoadingProject,
    handleSyncData,
    openSyncModal,
    closeSyncMOdal,
    handleNavigateToAcumatica,
    handleResetProjectFinancialAndForecasting,
    handleSaveProjectFinancialAndForecasting,
    isSyncingData,
    isSyncModalOpen,
    taskColumns,
    taskTable,
    revenueColumns,
    revenueTable,
    costColumns,
    costTable,
    transactionColumns,
    transactionTable,
    projectFinances,
    projectChangeLogTable,
    projectChangeLogColumns,
    financialAndForecastingTable,
    financialAndForecastingColumns,
    financialAndForecastingControlsId,
    setActiveTab,
    activeTab,
    transactionTotalItems,
    handleOpenHelpDocument,
    projectHealth,
    projectHealthHistory,
    isLoadingProjectHealth,
    marginTarget,
}: ProjectDetailsTemplateProps) => {
    return <div className="page-content">
        <Container fluid style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Breadcrumb
                title={"Project"}
                breadcrumbItem={"Project Details"}
                actions={<>
                    <ActionButton onClick={openSyncModal} disabled={isSyncingData}>
                        {isSyncingData ? <>Syncing <SyncIcon className="mdi-spin" /></> : <>Sync <SyncIcon /></>}
                    </ActionButton>
                    {
                        project &&
                        <ActionButton
                            style={{ gap: "5px" }}
                            onClick={() => handleNavigateToAcumatica(project)}
                        >
                            Open Acumatica
                            <img
                                src={AcumaticaLogo}
                                alt="Acumatica"
                                className="btn-with-icon__icon"
                                style={{ paddingLeft: '5px' }}
                            />
                        </ActionButton>
                    }
                    {handleOpenHelpDocument && (
                        <ActionButton onClick={() => handleOpenHelpDocument()}>
                            Help <HelpIcon />
                        </ActionButton>
                    )}
                </>}
            />
            {
                isLoadingProject || !project ?
                    <div className="section-container">
                        <LoadingState
                            title="Loading Project"
                            subtitle="Fetching project details… calibrating instruments..."
                        />
                    </div>
                    :
                    <>
                        <div className="project-overview-grid">
                        <DetailPageHeader
                            fullWidth
                            title={project?.code || "-"}
                            subtitle={project?.description}
                            rows={[
                                {
                                    label: "Project Manager",
                                    value: project?.projectManager
                                        ? `${project.projectManager.commonName || project.projectManager.firstName} ${project.projectManager.lastName}`
                                        : "-"
                                },
                                { label: "Customer", value: project?.client?.name || "-" },
                                {
                                    label: "Acumatica sync",
                                    value: projectHealth?.indicators.lastSyncAt
                                        ? formatDateTimeAmPm(projectHealth.indicators.lastSyncAt)
                                        : "-"
                                },
                            ]}
                            footer={<ProjectHealthSummary health={projectHealth} isLoading={isLoadingProjectHealth} />}
                        />
                        {
                            projectFinances && <FinancialCard
                                margin={projectFinances.marginPercent}
                                cost={projectFinances.cost}
                                revenue={projectFinances.revenue}
                                date={getMonthYearLabel(new Date())}
                                marginTarget={marginTarget}
                            />
                        }
                        </div>
                        <div className="section-container project-details-tabs">
                            {
                                isLoadingProject || !project ?
                                    <LoadingState
                                        title="Loading Project"
                                        subtitle="Pulling drawings, models, and spreadsheets together..."
                                    />
                                    :
                                    <Tabs
                                        setActiveTab={setActiveTab}
                                        activeTab={activeTab}
                                        headers={[
                                            "Health",
                                            "Financials And Forecasting",
                                            "Tasks",
                                            "Revenue",
                                            "Cost",
                                            "Transactions",
                                            // "Change Log",
                                            "Terms",
                                            // "Earned Value Analysis"
                                        ]}
                                        tabs={{
                                            "Health": <ProjectHealthPanel health={projectHealth} history={projectHealthHistory} isLoading={isLoadingProjectHealth} />,
                                            "Financials And Forecasting": <FinancialsAndForecasting
                                                handleReset={handleResetProjectFinancialAndForecasting}
                                                handleSave={handleSaveProjectFinancialAndForecasting}
                                                table={financialAndForecastingTable}
                                                column={financialAndForecastingColumns}
                                                project={project}
                                                controlsId={financialAndForecastingControlsId}
                                            />,
                                            "Tasks": <DataTable table={taskTable} columns={taskColumns} />,
                                            "Revenue": <DataTable table={revenueTable} columns={revenueColumns} />,
                                            "Cost": <DataTable table={costTable} columns={costColumns} />,
                                            "Transactions": <>
                                                <p className="text-muted mb-2">{transactionTotalItems} transaction(s)</p>
                                                <DataTable table={transactionTable} columns={transactionColumns} />
                                            </>
                                            ,
                                            // "Change Log": <ProjectChangeLog table={projectChangeLogTable} column={projectChangeLogColumns} />,
                                            "Terms": <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    justifyContent: 'space-evenly'
                                                }}
                                            >
                                                <div style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "250px 1fr",
                                                    rowGap: "12px",
                                                    columnGap: "20px",
                                                }}>
                                                    <label style={{ fontWeight: 600 }}>Description:</label>
                                                    <div>{project?.terms?.description || "-"}</div>

                                                    <label style={{ fontWeight: 600 }}>Due Type:</label>
                                                    <div>{project?.terms?.dueType || "-"}</div>

                                                    <label style={{ fontWeight: 600 }}>Installment Type:</label>
                                                    <div>{project?.terms?.installmentType}</div>

                                                    <label style={{ fontWeight: 600 }}>Installment Frequency:</label>
                                                    <div>{project?.terms?.installmentFrequency}</div>

                                                    <label style={{ fontWeight: 600 }}>Prepayment Required:</label>
                                                    <div>{project?.terms?.prepaymentRequired ? "Yes" : "No"}</div>

                                                    <label style={{ fontWeight: 600 }}>Prepayment %:</label>
                                                    <div>{project?.terms?.prepaymentPercent?.toString()}%</div>
                                                </div>
                                                <div style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "250px 1fr",
                                                    rowGap: "12px",
                                                    columnGap: "20px",
                                                }}>
                                                    <label style={{ fontWeight: 600 }}>Discount %:</label>
                                                    <div>{project?.terms?.discPercent ? `${project.terms.discPercent}%` : "-"}</div>

                                                    <label style={{ fontWeight: 600 }}>Discount Days:</label>
                                                    <div>{project?.terms?.dayDisc}</div>

                                                    <label style={{ fontWeight: 600 }}>Day Due 00:</label>
                                                    <div>{project?.terms?.dayDue00}</div>

                                                    <label style={{ fontWeight: 600 }}>Day Due 01:</label>
                                                    <div>{project?.terms?.dayDue01}</div>
                                                </div>
                                            </div>,
                                            // "Earned Value Analysis": <EarnedValueAnalysis />
                                        }} />
                            }
                        </div>

                    </>
            }
        </Container >
        <AreYouSureModal
            modalTitle={"Sync Project"}
            subTitle={"Sync project transactions, budgets, and tasks"}
            isOpen={isSyncModalOpen}
            onModalClose={closeSyncMOdal}
            handleAreYouSureDecline={closeSyncMOdal}
            handleAreYouSureAccept={handleSyncData}
            areYouSureacceptText={"Sync Data"}
            areYouSuredeclineText={"Cancel"}
        />
    </div >
}


export default ProjectDetailsTemplate